import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    TextField,
    InputAdornment,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Pagination,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Divider,
    IconButton,
    Stack,
    Tooltip
} from '@mui/material';
import {
    Search as SearchIcon,
    OpenInNew as OpenInNewIcon,
    ArrowBack as ArrowBackIcon,
    YouTube as YouTubeIcon,
    Instagram as InstagramIcon,
    Facebook as FacebookIcon,
    Podcasts as PodcastIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import TikTokIcon from '../components/icons/TikTokIcon';
import apiService from '../services/api';
import { formatChileanDate } from '../utils/dateUtils';

// Custom hook to fetch brand data
const useBrand = (brandId?: string) => {
    return useQuery({
        queryKey: ['brand', brandId],
        queryFn: () => brandId ? apiService.getBrand(brandId) : null,
        enabled: !!brandId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

// Component to display brand name with fallback
const BrandName: React.FC<{ mention: BrandMention }> = ({ mention }) => {
    const { data: brandData, isLoading } = useBrand(mention.brandId);

    if (isLoading) {
        return <Typography variant="body2" fontWeight="bold">{mention.brandName}</Typography>;
    }

    const displayName = brandData?.brand?.name || mention.brandName;

    return <Typography variant="body2" fontWeight="bold">{displayName}</Typography>;
};

interface Episode {
    _id: string;
    title: string;
    description?: string;
    podcastId: string;
    episodeNumber?: number;
    season?: number;
    duration?: number;
    publishedAt?: string;
    sponsorCount?: number;
    hasContentAnalysis?: boolean;
}

interface BrandMention {
    _id: string;
    contentId: string;
    brandId?: string;
    rawMentionName?: string;
    brandName: string;
    startTime: number;
    endTime: number;
    duration: number;
    context: string;
    confidence: number;
    commercialConfidence?: number;
    category?: string;
    classification: 'paid' | 'organic' | 'unknown';
    fragmentIndex?: number;
    createdAt: string;
    updatedAt: string;
    content?: {
        _id: string;
        title: string;
        url?: string;
        publishedAt: string;
        source: string;
        mediaType: string;
        thumbnail?: string;
        duration?: number;
    };
}



// Component for individual episode with expandable brand mentions
interface EpisodeWithMentionsProps {
    episode: Episode;
    isExpanded: boolean;
    onToggle: () => void;
    useEpisodeBrandMentions: (episodeId: string, enabled: boolean) => any;
    formatTime: (seconds: number) => string;
    getClassificationColor: (classification: string) => string;
    createYouTubeUrlWithTimestamp: (url: string, startTime: number) => string;
    formatChileanDate: (date: string) => string;
    showDivider: boolean;
}

const EpisodeWithMentions: React.FC<EpisodeWithMentionsProps> = ({
    episode,
    isExpanded,
    onToggle,
    useEpisodeBrandMentions,
    formatTime,
    getClassificationColor,
    createYouTubeUrlWithTimestamp,
    formatChileanDate,
    showDivider
}) => {
    const { data: mentionsData, isLoading: mentionsLoading } = useEpisodeBrandMentions(episode._id, isExpanded);
    const mentions = mentionsData?.mentions || [];

    const getSourceIcon = (source: string) => {
        switch (source) {
            case 'youtube':
                return <YouTubeIcon sx={{ color: '#FF0000' }} />;
            case 'instagram':
                return <InstagramIcon sx={{ color: '#E4405F' }} />;
            case 'tiktok':
                return <TikTokIcon sx={{ color: '#000000' }} />;
            case 'facebook':
                return <FacebookIcon sx={{ color: '#1877F2' }} />;
            default:
                return <PodcastIcon />;
        }
    };


    const handleCopyId = (id: string) => {
        navigator.clipboard.writeText(id);
    };

    return (
        <>
            {/* Episode Header */}
            <ListItem
                onClick={onToggle}
                sx={{
                    borderRadius: 1,
                    mb: 1,
                    cursor: 'pointer',
                    backgroundColor: episode.hasContentAnalysis ? 'grey.100' : 'transparent',
                    '&:hover': {
                        backgroundColor: episode.hasContentAnalysis ? 'grey.200' : 'action.hover',
                    },
                }}
            >
                <ListItemAvatar>
                    <Avatar>
                        <PodcastIcon />
                    </Avatar>
                </ListItemAvatar>
                <ListItemText
                    primary={
                        <Box>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle2">
                                    {episode.title}
                                </Typography>
                                {episode.hasContentAnalysis && (
                                    <Chip
                                        label="Analyzed"
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem', height: '20px' }}
                                    />
                                )}
                            </Box>
                            <Typography variant="caption" color="text.secondary" onClick={() => handleCopyId(episode._id)}>
                                {episode._id}
                            </Typography>
                        </Box>
                    }
                    secondary={
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                {episode.publishedAt && formatChileanDate(episode.publishedAt)}
                            </Typography>
                            {episode.duration && (
                                <Typography variant="caption" color="text.secondary">
                                    {' • '}{formatTime(episode.duration)}
                                </Typography>
                            )}
                        </Box>
                    }
                />
                <IconButton size="small">
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </ListItem>

            {/* Brand Mentions (when expanded) */}
            {isExpanded && (
                <Box sx={{ ml: 4, mr: 2, mb: 2 }}>
                    {mentionsLoading ? (
                        <Box display="flex" justifyContent="center" p={2}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : mentions.length === 0 ? (
                        <Alert severity="info" sx={{ mb: 1 }}>
                            No brand mentions found for this episode
                        </Alert>
                    ) : (
                        <List dense>
                            {mentions
                                .sort((a: BrandMention, b: BrandMention) => a.startTime - b.startTime) // Sort by start time (earliest to latest)
                                .map((mention: BrandMention, mentionIndex: number) => (
                                    <React.Fragment key={mention._id}>
                                        <ListItem
                                            sx={{
                                                borderRadius: 1,
                                                backgroundColor: 'grey.50',
                                                mb: 1,
                                                '&:hover': {
                                                    backgroundColor: 'grey.100',
                                                },
                                            }}
                                        >
                                            <ListItemAvatar>
                                                <Avatar sx={{ width: 32, height: 32 }}>
                                                    {mention.content && getSourceIcon(mention.content.source)}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <BrandName mention={mention} />
                                                        <Chip
                                                            label={mention.classification}
                                                            size="small"
                                                            color={getClassificationColor(mention.classification) as any}
                                                        />
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                                            {mention.context.substring(0, 80)}...
                                                        </Typography>
                                                        <br />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatTime(mention.startTime)} - {formatTime(mention.endTime)} •
                                                            {' '}{(mention.confidence * 100).toFixed(0)}% confidence
                                                            {mention.commercialConfidence && (
                                                                <span> • {mention.commercialConfidence.toFixed(0)}% commercial confidence</span>
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                            {mention.content?.url && (
                                                <Tooltip title="Open in YouTube">
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const url = createYouTubeUrlWithTimestamp(
                                                                mention.content!.url!,
                                                                mention.startTime
                                                            );
                                                            window.open(url, '_blank');
                                                        }}
                                                        size="small"
                                                    >
                                                        <OpenInNewIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </ListItem>
                                    </React.Fragment>
                                ))}
                        </List>
                    )}
                </Box>
            )}

            {showDivider && <Divider />}
        </>
    );
};

const PodcastBrandMentions: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();

    // State for filters and pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [brandFilter, setBrandFilter] = useState('');
    const [classificationFilter, setClassificationFilter] = useState('');
    const [page, setPage] = useState(1);
    const [episodePage, setEpisodePage] = useState(1);

    // State for expanded episodes
    const [expandedEpisodes, setExpandedEpisodes] = useState<Set<string>>(new Set());

    // Fetch podcast data
    const {
        data: podcast,
        isLoading: podcastLoading,
        error: podcastError,
    } = useQuery({
        queryKey: ['podcast-by-slug', slug],
        queryFn: async () => {
            const response = await apiService.getPodcastBySlug(slug || '');
            return response;
        },
        enabled: !!slug,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    // Fetch episodes for this podcast
    const {
        data: episodesData,
        isLoading: episodesLoading,
        error: episodesError,
    } = useQuery({
        queryKey: ['podcast-episodes', podcast?._id],
        queryFn: async () => {
            const response = await apiService.getPodcastEpisodes(podcast?._id || '');
            return response;
        },
        enabled: !!podcast?._id,
        staleTime: 0
    });

    console.log('episodesData', episodesData);

    // Fetch brand mentions for this podcast
    const {
        data: mentionsData,
    } = useQuery({
        queryKey: ['podcast-brand-mentions', podcast?._id, page, brandFilter, classificationFilter],
        queryFn: async () => {
            // This would be a new API endpoint to get brand mentions for a specific podcast
            const response = await apiService.getPodcastBrandMentions(
                podcast?._id || '',
                page,
                20,
                brandFilter || undefined,
                classificationFilter || undefined
            );
            return response;
        },
        enabled: !!podcast?._id,
        staleTime: 2 * 60 * 1000, // 2 minutes
    });

    // Filter episodes by search term locally
    const episodes: Episode[] = useMemo(() => {
        if (!episodesData?.episodes) return [];
        if (!searchTerm) return episodesData.episodes;
        return episodesData.episodes.filter((episode: Episode) =>
            episode.title.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [episodesData, searchTerm]);

    // Custom hook to fetch brand mentions for expanded episodes
    const useEpisodeBrandMentions = (episodeId: string, enabled: boolean) => {
        return useQuery({
            queryKey: ['episode-brand-mentions', episodeId, brandFilter, classificationFilter],
            queryFn: async () => {
                // Use the new episode-specific API endpoint
                const response = await apiService.getEpisodeBrandMentions(
                    podcast?._id || '',
                    episodeId,
                    1,
                    100, // Get all mentions for the episode
                    brandFilter || undefined,
                    classificationFilter || undefined
                );
                return response;
            },
            enabled: enabled && !!podcast?._id && !!episodeId,
            staleTime: 2 * 60 * 1000, // 2 minutes
        });
    };

    const totalEpisodePages = 1; // No pagination for episodes

    // Get unique brands for filter
    const uniqueBrands = useMemo(() => {
        const mentions = mentionsData?.mentions || [];
        const brands = new Set<string>();
        mentions.forEach((mention) => {
            if (mention.brandName) {
                brands.add(mention.brandName);
            }
        });
        return Array.from(brands).sort();
    }, [mentionsData?.mentions]);

    // Toggle episode expansion
    const toggleEpisodeExpansion = (episodeId: string) => {
        setExpandedEpisodes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(episodeId)) {
                newSet.delete(episodeId);
            } else {
                newSet.add(episodeId);
            }
            return newSet;
        });
    };

    const createYouTubeUrlWithTimestamp = (url: string, startTime: number): string => {
        if (!url) return '';
        const urlObj = new URL(url);
        urlObj.searchParams.set('t', Math.floor(startTime).toString() + 's');
        return urlObj.toString();
    };

    const getClassificationColor = (classification: string) => {
        switch (classification) {
            case 'paid':
                return 'success';
            case 'organic':
                return 'info';
            default:
                return 'default';
        }
    };

    const formatTime = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };


    if (podcastLoading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (podcastError || !podcast) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Alert severity="error">
                    Error loading podcast or podcast not found
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Header with back button */}
            <Box display="flex" alignItems="center" gap={2} mb={3}>
                <IconButton onClick={() => navigate('/explorar/brands')} size="large">
                    <ArrowBackIcon />
                </IconButton>
                <Box>
                    <Typography variant="h4" component="h1">
                        🎧 {podcast.title} - Brand Mentions
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Explore brand mentions across all episodes
                    </Typography>
                </Box>
            </Box>

            {/* Podcast Info Card */}
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={3}>
                        <Avatar
                            src={podcast.imageUrl}
                            sx={{ width: 80, height: 80 }}
                        >
                            <PodcastIcon sx={{ fontSize: 40 }} />
                        </Avatar>
                        <Box>
                            <Typography variant="h6">{podcast.title}</Typography>
                            {podcast.author && (
                                <Typography variant="body2" color="text.secondary">
                                    by {podcast.author}
                                </Typography>
                            )}
                            {podcast.description && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    {podcast.description}
                                </Typography>
                            )}
                            <Box display="flex" gap={1} mt={1}>
                                {podcast.category && (
                                    <Chip label={podcast.category} size="small" color="primary" />
                                )}
                                {podcast.episodeCount && (
                                    <Chip label={`${podcast.episodeCount} episodes`} size="small" variant="outlined" />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            <Grid container spacing={3}>
                {/* Episodes Panel */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Episodes
                            </Typography>

                            {/* Episode Search */}
                            <TextField
                                fullWidth
                                placeholder="Search episodes..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setEpisodePage(1);
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 3 }}
                            />

                            {/* Episodes List with Expandable Brand Mentions */}
                            {episodesLoading ? (
                                <Box display="flex" justifyContent="center" p={3}>
                                    <CircularProgress />
                                </Box>
                            ) : episodesError ? (
                                <Alert severity="error">Failed to load episodes</Alert>
                            ) : episodes.length === 0 ? (
                                <Alert severity="info">No episodes found</Alert>
                            ) : (
                                <>
                                    <List>
                                        {episodes.map((episode, index) => {
                                            const isExpanded = expandedEpisodes.has(episode._id);
                                            return (
                                                <EpisodeWithMentions
                                                    key={episode._id}
                                                    episode={episode}
                                                    isExpanded={isExpanded}
                                                    onToggle={() => toggleEpisodeExpansion(episode._id)}
                                                    useEpisodeBrandMentions={useEpisodeBrandMentions}
                                                    formatTime={formatTime}
                                                    getClassificationColor={getClassificationColor}
                                                    createYouTubeUrlWithTimestamp={createYouTubeUrlWithTimestamp}
                                                    formatChileanDate={formatChileanDate}
                                                    showDivider={index < episodes.length - 1}
                                                />
                                            );
                                        })}
                                    </List>

                                    {/* Episode Pagination */}
                                    {totalEpisodePages > 1 && (
                                        <Box display="flex" justifyContent="center" mt={2}>
                                            <Pagination
                                                count={totalEpisodePages}
                                                page={episodePage}
                                                onChange={(_, newPage) => setEpisodePage(newPage)}
                                                color="primary"
                                            />
                                        </Box>
                                    )}
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right Panel - Filters */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Brand Mention Filters
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Use these filters to narrow down brand mentions shown in episodes below.
                            </Typography>

                            {/* Brand Mention Filters */}
                            <Stack spacing={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Brand</InputLabel>
                                    <Select
                                        value={brandFilter}
                                        label="Brand"
                                        onChange={(e) => {
                                            setBrandFilter(e.target.value);
                                            setPage(1);
                                        }}
                                    >
                                        <MenuItem value="">All Brands</MenuItem>
                                        {uniqueBrands.map((brand) => (
                                            <MenuItem key={brand} value={brand}>
                                                {brand}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth size="small">
                                    <InputLabel>Classification</InputLabel>
                                    <Select
                                        value={classificationFilter}
                                        label="Classification"
                                        onChange={(e) => {
                                            setClassificationFilter(e.target.value);
                                            setPage(1);
                                        }}
                                    >
                                        <MenuItem value="">All Types</MenuItem>
                                        <MenuItem value="paid">Paid</MenuItem>
                                        <MenuItem value="organic">Organic</MenuItem>
                                        <MenuItem value="unknown">Unknown</MenuItem>
                                    </Select>
                                </FormControl>
                            </Stack>

                            {/* Instructions */}
                            <Alert severity="info" sx={{ mt: 3 }}>
                                Click on any episode to expand and view its brand mentions.
                                Use the filters above to refine which mentions are shown.
                            </Alert>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
};

export default PodcastBrandMentions;
